import { io } from "socket.io-client";
import { deviceToken } from "./api";

/**
 * The shop's live connection: new pre-orders and status changes, pushed.
 *
 * One socket for the whole app, opened lazily by the first subscriber and
 * authenticated with this install's device token — the same licence its REST
 * calls carry, so it only ever hears its own shop.
 *
 * Reconnection is automatic. Subscribers to "connect" are called after every
 * (re)connection, which is their cue to reload anything they may have missed
 * while the connection was down.
 */
const API_URL = new URL(
  process.env.REACT_APP_API_URL || "http://localhost:3010/api",
);
const PATH = `${API_URL.pathname.replace(/\/$/, "")}/socket.io`;

let socket = null;
const listeners = new Map();

const notify = (event, payload) =>
  listeners.get(event)?.forEach((handler) => handler(payload));

const ensureSocket = () => {
  const token = deviceToken.get();
  if (!token) return null;

  if (socket && socket.auth?.deviceToken === token) return socket;

  socket?.disconnect();

  socket = io(API_URL.origin, {
    path: PATH,
    auth: { deviceToken: token },
    transports: ["websocket", "polling"],
    reconnectionDelayMax: 10000,
  });

  socket.on("connect", () => notify("connect"));
  socket.onAny((event, payload) => notify(event, payload));

  return socket;
};

/** Listens for one event; returns the function that stops listening. */
export const subscribe = (event, handler) => {
  ensureSocket();

  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(handler);

  return () => listeners.get(event)?.delete(handler);
};
